import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Users,
  Eye,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Phone,
  Banknote,
  RotateCcw,
  CreditCard,
  ChevronRight,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import SearchableSelect from '../components/SearchableSelect';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editCust, setEditCust] = useState(null);
  const [deleteCust, setDeleteCust] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    phone: '',
    address: '',
    we_owe: '0',
    they_owe: '0',
    advance_payment_method: 'cash',
    notes: '',
  });
  const [activeTab, setActiveTab] = useState('customers');
  const [payments, setPayments] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [deletePayment, setDeletePayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    customer_id: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash',
    payment_type: 'received',
    amount: '',
    notes: '',
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (activeTab === 'payments') loadPayments();
  }, [activeTab]);

  const loadCustomers = async () => {
    try {
      let url = '/customers?limit=500';
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      const list = Array.isArray(res) ? res : (res?.customers || []);
      setCustomers(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load customers');
    }
  };

  const loadPayments = async () => {
    try {
      const res = await api.get('/customer-payments?limit=500');
      setPayments(Array.isArray(res) ? res : (res?.payments || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load payments');
    }
  };

  const filtered = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.phone && c.phone.includes(search))
  );

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    loadCustomers();
  };

  const applyDateFilter = () => {
    loadCustomers();
  };

  const openAdd = () => {
    setAddForm({ name: '', phone: '', address: '', we_owe: '0', they_owe: '0', advance_payment_method: 'cash', notes: '' });
    setShowAdd(true);
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    try {
      const theyOwe = parseFloat(addForm.they_owe) || 0;
      const weOwe = parseFloat(addForm.we_owe) || 0;
      await api.post('/customers', {
        name: addForm.name,
        phone: addForm.phone,
        address: addForm.address,
        pending_amount: theyOwe,
        advance_amount: weOwe,
        advance_payment_method: addForm.advance_payment_method,
        notes: addForm.notes,
      });
      toast.success(weOwe > 0 ? 'Customer and advance received added' : 'Customer added successfully');
      setShowAdd(false);
      loadCustomers();
    } catch (err) {
      toast.error(err.message || 'Failed to add customer');
    }
  };

  const openEdit = (cust) => {
    setEditCust(cust);
    const bal = Number(cust.balance || 0);
    setEditForm({
      ...cust,
      we_owe: bal < 0 ? String(Math.abs(bal)) : '0',
      they_owe: bal > 0 ? String(bal) : '0',
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const theyOwe = parseFloat(editForm.they_owe) || 0;
      const weOwe = parseFloat(editForm.we_owe) || 0;
      await api.put(`/customers/${editForm.id}`, {
        name: editForm.name,
        phone: editForm.phone,
        address: editForm.address,
        balance: theyOwe - weOwe,
        notes: editForm.notes,
      });
      toast.success('Customer updated successfully');
      setEditCust(null);
      loadCustomers();
    } catch (err) {
      toast.error(err.message || 'Failed to update customer');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/customers/${deleteCust.id}`);
      toast.success('Customer deleted');
      setDeleteCust(null);
      loadCustomers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete customer');
    }
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.customer_id) { toast.error('Please select a customer'); return; }
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const res = await api.post('/customer-payments', {
        customer_id: parseInt(paymentForm.customer_id),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        payment_type: paymentForm.payment_type || 'received',
        amount: parseFloat(paymentForm.amount),
        notes: paymentForm.notes,
        client_request_id: globalThis.crypto?.randomUUID?.() || `cp_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      });
      toast.success(res?.message || (paymentForm.payment_type === 'refund' ? 'Advance refund paid successfully' : 'Payment recorded successfully'));
      setShowPayment(false);
      setPaymentForm({
        customer_id: '',
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: 'cash',
        payment_type: 'received',
        amount: '',
        notes: '',
      });
      loadPayments();
      loadCustomers();
    } catch (err) {
      toast.error(err.message || 'Failed to record payment');
    }
  };

  const confirmDeletePayment = async () => {
    try {
      await api.delete(`/customer-payments/${deletePayment.id}`);
      toast.success('Payment deleted');
      setDeletePayment(null);
      loadPayments();
      loadCustomers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete payment');
    }
  };

  const customerOptions = customers.map((c) => ({ value: c.id, label: c.name }));

  const tabs = [
    { key: 'customers', label: 'Customers', icon: Users },
    { key: 'payments', label: 'Customer Payments', icon: CreditCard },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <p className="mt-1 text-sm text-gray-500">Manage customer profiles, outstanding dues, and payments received.</p>
      </div>

      <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={clsx('flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
              activeTab === key ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'customers' && (<>
        <div className="flex flex-wrap items-center justify-end gap-4">
          <button onClick={openAdd}
            className="flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]">
            <span className="text-lg leading-none">+</span> Add Customer
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers by name or phone..."
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 px-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
          </div>
        </div>

        <DateFilter dateFrom={dateFrom} dateTo={dateTo}
          onDateFromChange={setDateFrom} onDateToChange={setDateTo}
          onApply={applyDateFilter} onReset={resetFilters} />

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Registered Customers</h2>
            </div>
            <span className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              TOTAL : {filtered.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Customer Name</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Phone</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Address</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Advance</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Pending</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Note</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((cust, idx) => {
                  const bal = Number(cust.balance || 0);
                  const weOwe = bal < 0 ? Math.abs(bal) : 0;
                  const theyOwe = bal > 0 ? bal : 0;
                  return (
                    <tr key={cust.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                        <div
                          onClick={() => navigate(`/customers/${cust.id}`)}
                          className="flex items-center gap-3 cursor-pointer group/name"
                        >
                          <div className={clsx('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-transform group-hover/name:scale-105', avatarColors[idx % avatarColors.length])}>
                            {cust.name.charAt(0)}
                          </div>
                          <span className="text-sm font-semibold text-gray-900 group-hover/name:text-emerald-600 transition-colors">{cust.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-gray-700">
                          <Phone className="h-3.5 w-3.5 text-gray-400" />
                          {cust.phone || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[200px] truncate">{cust.address || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-red-600">
                        {weOwe > 0 ? `Rs. ${weOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={clsx('text-sm font-semibold', theyOwe > 0 ? 'text-emerald-600' : 'text-gray-400')}>
                          {theyOwe > 0 ? `Rs. ${theyOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })}` : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 max-w-[150px] truncate">{cust.notes || '-'}</td>
                      <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {theyOwe > 0 && (
                            <button
                              onClick={() => {
                                setPaymentForm({
                                  customer_id: String(cust.id),
                                  payment_date: new Date().toISOString().slice(0, 10),
                                  payment_method: 'cash',
                                  payment_type: 'received',
                                  amount: String(theyOwe),
                                  notes: '',
                                });
                                setShowPayment(true);
                              }}
                              className="flex items-center gap-1 rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                              title="Receive Payment"
                            >
                              <Banknote className="h-3.5 w-3.5" /> Pay
                            </button>
                          )}
                          {weOwe > 0 && (
                            <button
                              onClick={() => {
                                setPaymentForm({
                                  customer_id: String(cust.id),
                                  payment_date: new Date().toISOString().slice(0, 10),
                                  payment_method: 'cash',
                                  payment_type: 'refund',
                                  amount: String(weOwe),
                                  notes: 'Advance refund paid to customer',
                                });
                                setShowPayment(true);
                              }}
                              className="flex items-center gap-1 rounded-lg bg-rose-50 border border-rose-200 px-2 py-1 text-xs font-bold text-rose-700 transition-colors hover:bg-rose-100"
                              title="Refund Advance to Customer"
                            >
                              <RotateCcw className="h-3.5 w-3.5" /> Refund
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/customers/${cust.id}`)}
                            className="rounded-lg p-2 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
                            title="View Customer Details & Ledger"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={() => openEdit(cust)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600" title="Edit Profile">
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteCust(cust)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Users className="mb-3 h-12 w-12 text-gray-300" />
              <p className="text-sm">No customers yet. Click "Add Customer" to get started.</p>
            </div>
          )}
        </div>
      </>)}

      {activeTab === 'payments' && (<>
        <div className="flex justify-end gap-2">
          <button onClick={() => {
            setPaymentForm({
              customer_id: '',
              payment_date: new Date().toISOString().slice(0, 10),
              payment_method: 'cash',
              payment_type: 'received',
              amount: '',
              notes: '',
            });
            setShowPayment(true);
          }}
            className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 active:scale-[0.97]">
            <Banknote className="h-4 w-4" /> Receive Payment
          </button>
          <button onClick={() => {
            setPaymentForm({
              customer_id: '',
              payment_date: new Date().toISOString().slice(0, 10),
              payment_method: 'cash',
              payment_type: 'refund',
              amount: '',
              notes: 'Advance refund paid to customer',
            });
            setShowPayment(true);
          }}
            className="flex items-center gap-2 rounded-lg bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-600 active:scale-[0.97]">
            <RotateCcw className="h-4 w-4" /> Refund Advance
          </button>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-500" />
              <h2 className="text-base font-semibold text-gray-900">Payment History</h2>
            </div>
            <span className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              TOTAL : {payments.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Customer</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Type</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Method</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Amount</th>
                  <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Notes</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-400">No payments recorded</td></tr>
                )}
                {payments.map((p) => {
                  const isRefund = p.payment_type === 'refund';
                  return (
                    <tr key={p.id} className="group transition-colors hover:bg-gray-50/50">
                      <td className="sticky-col-left px-6 py-4 whitespace-nowrap text-sm text-gray-600">{fmtDate(p.payment_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                        {p.customer_id ? (
                          <Link
                            to={`/customers/${p.customer_id}`}
                            className="font-semibold text-emerald-600 hover:underline"
                          >
                            {p.customer_name || `Customer #${p.customer_id}`}
                          </Link>
                        ) : (
                          p.customer_name || '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isRefund ? (
                          <span className="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-700">
                            Advance Refund
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                            Received
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold capitalize text-blue-700">
                          {p.payment_method || 'cash'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={clsx('text-sm font-bold', isRefund ? 'text-rose-600' : 'text-emerald-600')}>
                          {isRefund ? '- ' : '+ '}Rs. {(parseFloat(p.amount) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-[200px] truncate">{p.notes || '-'}</td>
                      <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={() => setDeletePayment(p)}
                          className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* ADD MODAL */}
      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Customer</h3>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveNew} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Full Name *</label>
                    <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label>
                    <input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  {Number(addForm.we_owe) > 0 && <div className="col-span-2"><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance Payment Method</label><select value={addForm.advance_payment_method} onChange={(e) => setAddForm({ ...addForm, advance_payment_method: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"><option value="cash">Cash</option><option value="card">Card</option><option value="online">Online</option><option value="bank">Bank</option></select></div>}
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance (Rs.)</label>
                    <input type="number" min="0" value={addForm.we_owe} onChange={(e) => setAddForm({ ...addForm, we_owe: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Pending (Rs.)</label>
                    <input type="number" min="0" value={addForm.they_owe} onChange={(e) => setAddForm({ ...addForm, they_owe: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                    <input value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label>
                    <textarea value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Customer</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT MODAL */}
      {editCust &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditCust(null)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Customer</h3>
                <button onClick={() => setEditCust(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveEdit} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Full Name</label>
                    <input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label>
                    <input value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance (Rs.)</label>
                    <input type="number" min="0" value={editForm.we_owe || '0'} onChange={(e) => setEditForm({ ...editForm, we_owe: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Pending (Rs.)</label>
                    <input type="number" min="0" value={editForm.they_owe || '0'} onChange={(e) => setEditForm({ ...editForm, they_owe: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                    <input value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label>
                    <textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setEditCust(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CUSTOMER CONFIRM */}
      {deleteCust &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteCust(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Customer?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete <strong>{deleteCust.name}</strong>? This action cannot be undone.</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteCust(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* NEW PAYMENT / REFUND MODAL */}
      {showPayment && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPayment(false)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {paymentForm.payment_type === 'refund' ? 'Refund Advance to Customer' : 'Record Customer Payment'}
                </h3>
                <p className="text-xs text-gray-500">
                  {paymentForm.payment_type === 'refund'
                    ? 'Pay out money from account to customer & adjust advance balance'
                    : 'Record payment received from customer into account'}
                </p>
              </div>
              <button onClick={() => setShowPayment(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Type selector */}
            <div className="mb-4 flex rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => {
                  const selCust = customers.find((c) => String(c.id) === String(paymentForm.customer_id));
                  const bal = Number(selCust?.balance || 0);
                  const owed = bal > 0 ? bal : 0;
                  setPaymentForm({
                    ...paymentForm,
                    payment_type: 'received',
                    amount: owed > 0 ? String(owed) : '',
                    notes: paymentForm.notes === 'Advance refund paid to customer' ? '' : paymentForm.notes,
                  });
                }}
                className={clsx(
                  'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                  paymentForm.payment_type === 'received'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <Banknote className="h-3.5 w-3.5" /> Receive Payment (Cash In)
              </button>
              <button
                type="button"
                onClick={() => {
                  const selCust = customers.find((c) => String(c.id) === String(paymentForm.customer_id));
                  const bal = Number(selCust?.balance || 0);
                  const weOwe = bal < 0 ? Math.abs(bal) : 0;
                  setPaymentForm({
                    ...paymentForm,
                    payment_type: 'refund',
                    amount: weOwe > 0 ? String(weOwe) : '',
                    notes: !paymentForm.notes ? 'Advance refund paid to customer' : paymentForm.notes,
                  });
                }}
                className={clsx(
                  'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                  paymentForm.payment_type === 'refund'
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Refund Advance (Cash Out)
              </button>
            </div>

            {(() => {
              const selCust = customers.find((c) => String(c.id) === String(paymentForm.customer_id));
              const bal = Number(selCust?.balance || 0);
              const owed = bal > 0 ? bal : 0;
              const weOwe = bal < 0 ? Math.abs(bal) : 0;

              if (paymentForm.payment_type === 'refund') {
                return (
                  <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Advance Owed to Customer:</span>
                      <span className="text-sm font-extrabold">Rs. {weOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <p className="text-[11px] text-rose-700">
                      This payout will <strong>deduct money from your pharmacy cash/bank account</strong> and settle/reduce what you owe the customer.
                    </p>
                  </div>
                );
              }

              if (selCust) {
                return (
                  <div className={clsx(
                    'mb-4 rounded-xl border p-3 text-xs',
                    owed > 0 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  )}>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Customer Pending Dues:</span>
                      <span className="text-sm font-extrabold">Rs. {owed.toLocaleString('en-PK', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                );
              }

              return null;
            })()}

            <form onSubmit={handleSavePayment} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Customer *</label>
                  <SearchableSelect
                    value={paymentForm.customer_id ? String(paymentForm.customer_id) : ''}
                    onChange={(val) => {
                      const selCust = customers.find((c) => String(c.id) === String(val));
                      const bal = Number(selCust?.balance || 0);
                      const owed = bal > 0 ? bal : 0;
                      const weOwe = bal < 0 ? Math.abs(bal) : 0;
                      const autoAmount = paymentForm.payment_type === 'refund'
                        ? (weOwe > 0 ? String(weOwe) : '')
                        : (owed > 0 ? String(owed) : '');
                      setPaymentForm({
                        ...paymentForm,
                        customer_id: val,
                        amount: autoAmount,
                      });
                    }}
                    options={customers.map((c) => {
                      const bal = Number(c.balance || 0);
                      const owed = bal > 0 ? bal : 0;
                      const weOwe = bal < 0 ? Math.abs(bal) : 0;
                      return {
                        value: String(c.id),
                        label: c.name,
                        sublabel: weOwe > 0
                          ? `Advance: Rs. ${weOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })}`
                          : owed > 0
                            ? `Owes: Rs. ${owed.toLocaleString('en-PK', { minimumFractionDigits: 2 })}`
                            : c.phone ? `Phone: ${c.phone}` : null,
                      };
                    })}
                    placeholder="-- Select Customer --"
                    searchPlaceholder="Search customer by name..."
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Date</label>
                  <input type="date" value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method</label>
                  <div className="flex gap-2">
                    {['cash', 'card'].map((m) => (
                      <button type="button" key={m} onClick={() => setPaymentForm({ ...paymentForm, payment_method: m })}
                        className={clsx('flex-1 py-2 text-xs font-semibold rounded-lg border transition-colors capitalize',
                          paymentForm.payment_method === m
                            ? (paymentForm.payment_type === 'refund' ? 'bg-rose-500 text-white border-rose-500' : 'bg-emerald-500 text-white border-emerald-500')
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100')}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Amount (Rs.) *</label>
                  <input type="number" step="0.01" min="0.01" value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder={(() => {
                      const selCust = customers.find((c) => String(c.id) === String(paymentForm.customer_id));
                      const bal = Number(selCust?.balance || 0);
                      const owed = bal > 0 ? bal : 0;
                      const weOwe = bal < 0 ? Math.abs(bal) : 0;
                      return paymentForm.payment_type === 'refund'
                        ? (weOwe > 0 ? `Max advance: ${weOwe}` : 'Enter amount')
                        : (owed > 0 ? `Total due: ${owed}` : 'Enter amount');
                    })()}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                  {(() => {
                    const selCust = customers.find((c) => String(c.id) === String(paymentForm.customer_id));
                    const bal = Number(selCust?.balance || 0);
                    const owed = bal > 0 ? bal : 0;
                    const weOwe = bal < 0 ? Math.abs(bal) : 0;
                    if (paymentForm.payment_type === 'refund' && weOwe > 0) {
                      return (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setPaymentForm({ ...paymentForm, amount: String(weOwe) })}
                            className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100"
                          >
                            Full Advance (Rs. {weOwe.toLocaleString('en-PK', { minimumFractionDigits: 2 })})
                          </button>
                        </div>
                      );
                    }
                    if (paymentForm.payment_type === 'received' && owed > 0) {
                      return (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setPaymentForm({ ...paymentForm, amount: String(owed) })}
                            className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
                          >
                            Full Dues (Rs. {owed.toLocaleString('en-PK', { minimumFractionDigits: 2 })})
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                <textarea rows={2} value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder={paymentForm.payment_type === 'refund' ? 'Reason for advance return / payout...' : 'Optional payment notes...'}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button type="button" onClick={() => setShowPayment(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit"
                  className={clsx(
                    'flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors',
                    paymentForm.payment_type === 'refund'
                      ? 'bg-rose-500 hover:bg-rose-600'
                      : 'bg-emerald-500 hover:bg-emerald-600'
                  )}>
                  {paymentForm.payment_type === 'refund' ? (
                    <><RotateCcw className="h-4 w-4" /> Refund & Deduct from Account</>
                  ) : (
                    <><Banknote className="h-4 w-4" /> Record Payment</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* DELETE PAYMENT CONFIRM */}
      {deletePayment && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeletePayment(null)} />
          <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Delete Payment?</h3>
            <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete this payment of <strong>Rs. {(parseFloat(deletePayment.amount) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2 })}</strong> from <strong>{deletePayment.customer_name}</strong>? The customer's balance will be adjusted.</p>
            <div className="mt-6 flex justify-center gap-3">
              <button onClick={() => setDeletePayment(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDeletePayment} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
